import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { markPasskeyUsedOnDevice } from '../../utils/passkeyUtils';
import styles from './LoginReg.module.scss';
import {
  IonContent,
  IonPage,
  useIonRouter,
  IonToast,
} from '@ionic/react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts';
import { getDashboardRoute } from '@rsmaids/shared/constants/routes';
import { AUTH_MESSAGES } from '@rsmaids/shared/constants/auth';
import { STORAGE_KEYS, ERROR_KEYWORDS } from '@rsmaids/shared/constants/common';
import { validateRegistration, validateLogin } from '../../utils/validation';
import { handleAuthError } from '../../utils/errorHandling';
import { MainCard } from '../../components/AuthComponents/MainCard';
import { LoginCard } from '../../components/AuthComponents/LoginCard';
import { PasswordCard } from '../../components/AuthComponents/PasswordCard';
import { RegisterCard } from '../../components/AuthComponents/RegisterCard';
import { RegisterPasswordCard } from '../../components/AuthComponents/RegisterPasswordCard';
import { ResetCard } from '../../components/AuthComponents/ResetCard';
import { PasskeyAuth } from '../../components/AuthComponents/PasskeyAuth';
import { VerificationCodeCard } from '../../components/AuthComponents/VerificationCodeCard';
import { rsAuth } from '@rsmaids/shared/services/rsAuthClient';
import { requestNotificationPermission } from '../../utils/notificationUtils';
import { useTranslation } from 'react-i18next';

// i18n-ignore: CSS class names, not text
const TOAST_CLASS_OK = 'app-toast';
// i18n-ignore: CSS class names, not text
const TOAST_CLASS_ERROR = 'app-toast app-toast--error';

const LoginReg: React.FC = () => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const location = useLocation();
  const { authState, actions } = useAuth();
  const { loginBackend, registerBackend, resendVerification, verifyEmailCode, verifyEmployeeCode } = actions;
  const { isLoggedIn, user, isLoading: authLoading } = authState;
  const [activeCard, setActiveCard] = useState<'main' | 'login' | 'password' | 'register' | 'registerPassword' | 'reset' | 'passkey' | 'passkeySetup' | 'verifyCode'>('main');

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: ''
  });

  // UI state
  const [uiState, setUiState] = useState({
    showAllAuthOptions: false,
    showPassword: false,
    rememberUser: false,
    loggingIn: false,
    registering: false,
    showResendVerification: false
  });

  // Verification mode state
  const [verificationMode, setVerificationMode] = useState<'email' | 'employee'>('email');

  // True while a verification code is being checked: the Verify button turns into a loader
  const [isVerifying, setIsVerifying] = useState(false);
  // The code box auto-submits on the last character and the Verify button submits the same code again; state updates too late
  // to stop that second call, a ref does not.
  const verifyingRef = useRef(false);

  // Toast state
  const [toastState, setToastState] = useState({
    show: false,
    message: '',
    color: 'danger' as string,
    duration: 3000
  });

  // Computed values to eliminate repetitive calculations
  const isDisabled = useMemo(() => uiState.loggingIn || authLoading, [uiState.loggingIn, authLoading]);
  const hasValidEmail = useMemo(() => Boolean(formData.email?.trim()), [formData.email]);

  useEffect(() => {
    // Add class to hide nav bar gradient on login page
    document.body.classList.add('login-route');
    // Hide native Android nav bar scrim on login page
    const na = (window as unknown as { NativeAndroid?: { setScrimVisible: (v: boolean) => void } }).NativeAndroid;
    if (na) na.setScrimVisible(false);
    return () => {
      document.body.classList.remove('login-route');
      // Restore native scrim when leaving login page
      if (na) na.setScrimVisible(true);
    };
  }, []);

  useEffect(() => {
    // Only redirect from the login route itself. IonRouterOutlet can keep
    // pages mounted, so this guard prevents stale login effects from
    // hijacking navigation on other tabs/routes.
    if (location.pathname !== '/login') {
      return;
    }

    // Always redirect authenticated users away from /login.
    if (authLoading || !isLoggedIn || !user) {
      return;
    }

    const dashboardRoute = getDashboardRoute(user.role);
    if (window.location.pathname !== dashboardRoute) {
      router.push(dashboardRoute, 'root', 'replace');
    }
  }, [authLoading, isLoggedIn, user, router, location.pathname]);

  // Helper functions for state updates
  const updateFormData = useCallback((field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateUIState = useCallback((field: keyof typeof uiState, value: boolean) => {
    setUiState(prev => ({ ...prev, [field]: value }));
  }, []);

  // Load saved username when component mounts
  useEffect(() => {
    const savedUsername = localStorage.getItem(STORAGE_KEYS.REMEMBERED_USERNAME);
    if (savedUsername) {
      Promise.resolve().then(() => {
        updateFormData('email', savedUsername);
        updateUIState('rememberUser', true);
      });
    }
  }, [updateFormData, updateUIState]);

  const resetForm = useCallback(() => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: ''
    });
  }, []);

  const clearPassword = useCallback(() => {
    setFormData(prev => ({ ...prev, password: '' }));
  }, []);

  const handleSuccessfulLogin = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.SHOW_DASHBOARD_TOAST, 'true');

    if (uiState.rememberUser) {
      localStorage.setItem(STORAGE_KEYS.REMEMBERED_USERNAME, formData.email);
    }

    updateUIState('loggingIn', false);
    clearPassword();
  }, [uiState.rememberUser, formData.email, clearPassword, updateUIState]);

  const displayToast = useCallback((message: string, color: string = 'danger', duration: number = 3000) => {
    setToastState({
      show: true,
      message,
      color,
      duration
    });
  }, []);

  // Handle direct passkey login from LoginCard (inline, no navigation)
  const handleDirectPasskeyLogin = async () => {
    if (!formData.email) {
      displayToast(t('pages.login.LoginReg.pleaseEnterYourEmailAddress'), 'warning');
      return;
    }

    updateUIState('loggingIn', true);
    try {
      // Import passkey login logic directly
      const startResult = await rsAuth.startPasskeyLogin(formData.email);

      if (!startResult.success || !startResult.options) {
        throw new Error('Failed to start passkey login');
      }

      // Convert challenge from base64url string to Uint8Array for WebAuthn API
      const options = startResult.options as unknown as { challenge: string; allowCredentials?: PublicKeyCredentialDescriptor[]; userVerification?: UserVerificationRequirement; timeout?: number; rpId?: string };
      const originalChallenge = options.challenge;
      let challengeBytes: Uint8Array;
      if (typeof options.challenge === 'string') {
        const challengeStr = options.challenge;
        const binaryString = atob(challengeStr.replace(/-/g, '+').replace(/_/g, '/'));
        challengeBytes = new Uint8Array(new ArrayBuffer(binaryString.length));
        for (let i = 0; i < binaryString.length; i++) {
          challengeBytes[i] = binaryString.charCodeAt(i);
        }
      } else {
        challengeBytes = new Uint8Array(options.challenge as ArrayBuffer);
      }

      const requestOptions: PublicKeyCredentialRequestOptions = {
        challenge: challengeBytes as BufferSource,
        allowCredentials: options.allowCredentials || [],
        userVerification: options.userVerification || 'preferred',
        timeout: options.timeout || 60000,
        rpId: options.rpId
      };

      // Use WebAuthn API to authenticate
      const credential = await navigator.credentials.get({
        publicKey: requestOptions
      }) as PublicKeyCredential | null;

      if (!credential) {
        throw new Error('Passkey authentication was cancelled');
      }

      // Convert the credential response
      const assertion = credential.response as AuthenticatorAssertionResponse;

      const arrayBufferToBase64url = (buffer: ArrayBuffer): string => {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        bytes.forEach(byte => binary += String.fromCharCode(byte));
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      };

      const assertionResponse = {
        id: credential.id,
        rawId: arrayBufferToBase64url(credential.rawId),
        response: {
          authenticatorData: arrayBufferToBase64url(assertion.authenticatorData),
          clientDataJSON: arrayBufferToBase64url(assertion.clientDataJSON),
          signature: arrayBufferToBase64url(assertion.signature),
          ...(assertion.userHandle && {
            userHandle: arrayBufferToBase64url(assertion.userHandle)
          })
        },
        type: credential.type
      };

      // Finish passkey login
      const finishResult = await rsAuth.finishPasskeyLogin({
        credentialId: credential.id,
        assertion: assertionResponse,
        challenge: originalChallenge
      });

      if (!finishResult.success || !finishResult.user) {
        throw new Error('Passkey verification failed');
      }

      // Store user session data
      const userInfo = finishResult.user;
      const sessionData = {
        user: userInfo,
        firebaseToken: finishResult.user?.customToken || '',
        loginTimestamp: Date.now()
      };

      localStorage.setItem('session', JSON.stringify(sessionData));
      
      // Mark that user has successfully logged in with passkey
      markPasskeyUsedOnDevice(formData.email);

      // Sign in with Firebase
      if (finishResult.user.customToken) {
        const { signInWithCustomToken } = await import('firebase/auth');
        const { auth } = await import('../../auth/firebase');
        await signInWithCustomToken(auth, finishResult.user.customToken);
      }

      displayToast(AUTH_MESSAGES.PASSKEY_LOGIN_SUCCESS, 'success', 1500);
      // Auth state change will trigger navigation
    } catch (error) {
      console.error('Passkey login failed:', error);
      displayToast(error instanceof Error ? error.message : 'Passkey authentication failed', 'danger', 5000);
    } finally {
      updateUIState('loggingIn', false);
    }
  };

  const handleLoginClick = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    // Use utility function instead of repetitive validation
    const validation = validateLogin(formData.email, formData.password);
    if (!validation.isValid) {
      displayToast(validation.message || AUTH_MESSAGES.ENTER_EMAIL_PASSWORD);
      return;
    }
    
    updateUIState('loggingIn', true);
    
    // Trigger permission dialog from explicit user gesture.
    requestNotificationPermission({ prompt: true }).catch(() => {
      // Silently ignore permission errors
    });
    
    try {
      // Use Firebase Auth directly for password verification
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      const { auth } = await import('../../auth/firebase');
      
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      
      // Password verified! Now get user data from backend
      const result = await loginBackend();
      
      if (result.success) {
        updateUIState('showResendVerification', false); // Hide resend button after successful login
        handleSuccessfulLogin();
        return;
      } else {
        // Handle login errors
        if (result.error) {
          const errorString = typeof result.error === 'string' ? result.error : result.error.message || t('pages.login.LoginReg.unknownError');
          
          // Check for email verification requirements
          if (errorString.includes(ERROR_KEYWORDS.EMAIL_VERIFICATION_REQUIRED) || errorString.includes(ERROR_KEYWORDS.GRACE_PERIOD_EXPIRED)) {
            updateUIState('showResendVerification', true);
            displayToast(AUTH_MESSAGES.EMAIL_VERIFICATION_REQUIRED, 'warning', 5000);
          } else if (errorString.includes(ERROR_KEYWORDS.INVALID_CREDENTIAL) || errorString.includes(ERROR_KEYWORDS.WRONG_PASSWORD)) {
            displayToast(AUTH_MESSAGES.INCORRECT_CREDENTIALS);
          } else if (errorString.includes(ERROR_KEYWORDS.USER_NOT_FOUND)) {
            displayToast(AUTH_MESSAGES.NO_ACCOUNT_FOUND);
          } else {
            displayToast(errorString || AUTH_MESSAGES.LOGIN_FAILED_CREDENTIALS);
          }
        } else {
          displayToast(AUTH_MESSAGES.LOGIN_FAILED_CREDENTIALS);
        }
      }
    } catch (error: unknown) {
      await handleAuthError(error, { 
        showToast: displayToast, 
        context: 'login',
        onFinally: () => updateUIState('loggingIn', false)
      });
      return;
    }
    updateUIState('loggingIn', false);
  };

  const handleRegisterClick = async () => {
    // Use utility function instead of repetitive validation
    const validation = validateRegistration(formData);
    if (!validation.isValid) {
      displayToast(validation.message || AUTH_MESSAGES.ENTER_EMAIL_PASSWORD);
      return;
    }
    
    updateUIState('registering', true);
    
    try {
      // Call backend registration API
      const result = await registerBackend(formData.email, formData.password, formData.firstName, formData.lastName, formData.phone);
      
      if (result.success) {
        // Use the message from the backend response instead of hardcoded message
        const toastMessage = result.message || t('pages.login.LoginReg.registrationSuccessful');
        displayToast(toastMessage, 'success', 5000);

        // Keep the email in form for verification
        const registeredEmail = formData.email;
        resetForm();
        setFormData(prev => ({ ...prev, email: registeredEmail }));

        // Check if this is an employee with a pending code
        if (result.hasEmployeeCode && result.verificationType === 'employee-code') {
          // Employee has a pending code - go directly to employee code verification
          setVerificationMode('employee'); // Set to employee code mode
          setActiveCard('verifyCode');
        } else if (result.requiresEmailVerification) {
          // Regular user needs email verification
          setVerificationMode('email'); // Set to regular verification mode
          setActiveCard('verifyCode');
        } else {
          // Account is ready (shouldn't happen in current flow but good to handle)
          setActiveCard('login');
        }
        
        return;
      } else {
        // Handle registration errors
        if (result.error) {
          const errorString = typeof result.error === 'string' ? result.error : result.error.message || t('pages.login.LoginReg.unknownError');
          
          // Check for common registration issues
          if (errorString.includes(ERROR_KEYWORDS.EMAIL_ALREADY_IN_USE) || errorString.includes('ALREADY_EXISTS') || errorString.includes('already registered')) {
            displayToast(AUTH_MESSAGES.EMAIL_ALREADY_REGISTERED);
          } else if (errorString.includes(ERROR_KEYWORDS.WEAK_PASSWORD)) {
            displayToast(AUTH_MESSAGES.PASSWORD_TOO_WEAK);
          } else if (errorString.includes(ERROR_KEYWORDS.INVALID_EMAIL)) {
            displayToast(AUTH_MESSAGES.INVALID_EMAIL_ADDRESS);
          } else {
            displayToast(errorString || AUTH_MESSAGES.REGISTRATION_FAILED);
          }
        } else {
          displayToast(AUTH_MESSAGES.REGISTRATION_FAILED);
        }
      }
    } catch (error: unknown) {
      // Handle network/fetch errors gracefully
      const errorMessage = error instanceof Error ? error.message : t('pages.login.LoginReg.unknownError');
      if (errorMessage.includes('already registered') || errorMessage.includes('ALREADY_EXISTS')) {
        displayToast(AUTH_MESSAGES.EMAIL_ALREADY_REGISTERED);
      } else {
        displayToast('Registration failed. Please try again.');
      }
    } finally {
      updateUIState('registering', false);
    }
  };

  const handlePasswordResetClick = async () => {
    if (!formData.email) {
      displayToast(AUTH_MESSAGES.ENTER_EMAIL_FOR_RESET);
      return;
    }
    updateUIState('loggingIn', true);
    try {
      const result = await rsAuth.resetPassword(formData.email);
      if (result.success) {
        displayToast('Password reset email sent! Check your inbox.', 'success', 4000);
        setActiveCard('login');
      } else {
        const errorMessage = typeof result.error === 'string' ? result.error : result.error?.message || t('pages.login.LoginReg.failedToSendResetEmailPlease');
        displayToast(errorMessage);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      displayToast(AUTH_MESSAGES.UNEXPECTED_ERROR);
    } finally {
      updateUIState('loggingIn', false);
    }
  };

  const handleEmailVerification = async (code: string) => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setIsVerifying(true);
    try {
      const result = await verifyEmailCode(formData.email, code);
      if (result.success) {
        displayToast('Email verified successfully! You can now log in.', 'success', 4000);
        updateUIState('showResendVerification', false);
        setActiveCard('login');
      } else {
        displayToast(result.error?.toString() || t('pages.login.LoginReg.invalidVerificationCodePleaseTryAgain'), 'danger');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      displayToast(t('pages.login.LoginReg.failedToVerifyEmailPleaseTry'), 'danger');
    } finally {
      verifyingRef.current = false;
      setIsVerifying(false);
    }
  };

  const handleEmployeeCodeVerification = async (code: string) => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setIsVerifying(true);
    try {
      const result = await verifyEmployeeCode(formData.email, code);
      if (result.success) {
        displayToast('Employee role assigned successfully! You can now log in.', 'success', 4000);
        // Redirect to login or automatically log in depending on your flow
        setActiveCard('login');
      } else {
        displayToast(result.error?.toString() || t('pages.login.LoginReg.invalidEmployeeCodePleaseContactYour'), 'danger');
      }
    } catch {
      displayToast(t('pages.login.LoginReg.failedToVerifyEmployeeCodePlease'), 'danger');
    } finally {
      verifyingRef.current = false;
      setIsVerifying(false);
    }
  };



  const renderMainCard = () => (
    <MainCard
      cardClasses={`${styles.loginCard} ion-text-center`}
      cardHeaderClasses={`${styles.cardHeader} ion-text-center`}
      uiState={uiState}
      authLoading={authLoading}
      isDisabled={isDisabled}
      onLoginClick={() => setActiveCard('login')}
      onRegisterClick={() => setActiveCard('register')}
    />
  );

  const renderLoginCard = () => (
    <LoginCard
      formData={{ email: formData.email }}
      uiState={{ rememberUser: uiState.rememberUser }}
      isDisabled={isDisabled}
      onGoBack={() => { setActiveCard('main'); clearPassword(); }}
      onContinue={() => setActiveCard('password')}
      onInputChange={(field: string, value: string) => updateFormData(field as keyof typeof formData, value)}
      onCheckboxChange={(field: string, value: boolean) => updateUIState(field as keyof typeof uiState, value)}
      onPasskeyLogin={handleDirectPasskeyLogin}
      onPasskeyRegister={() => setActiveCard('passkeySetup')}
    />
  );

  const renderResetCard = () => (
    <ResetCard
      formData={{ email: formData.email }}
      isDisabled={isDisabled}
      hasValidEmail={hasValidEmail}
      onGoBack={() => setActiveCard('login')}
      onReset={handlePasswordResetClick}
      onInputChange={(field: string, value: string) => updateFormData(field as keyof typeof formData, value)}
    />
  );

  const renderPasswordCard = () => (
    <PasswordCard
      formData={{ password: formData.password }}
      uiState={{
        loggingIn: uiState.loggingIn,
        showPassword: uiState.showPassword,
        showResendVerification: uiState.showResendVerification
      }}
      isDisabled={isDisabled}
      hasValidEmail={hasValidEmail}
      onGoBack={() => setActiveCard('login')}
      onLogin={handleLoginClick}
      onPasswordReset={() => setActiveCard('reset')}
      onResendVerification={async () => {
        if (!formData.email) {
          displayToast(AUTH_MESSAGES.ENTER_EMAIL_ADDRESS_FIRST);
          return;
        }
        try {
          const result = await resendVerification(formData.email);
          if (result.success) {
            displayToast(AUTH_MESSAGES.VERIFICATION_EMAIL_SENT, 'success', 4000);
            // Redirect to verification card after successful resend
            setActiveCard('verifyCode');
          } else {
            displayToast(result.error?.toString() || t('pages.login.LoginReg.failedToSendVerificationEmail'));
          }
        } catch {
          displayToast(t('pages.login.LoginReg.failedToSendVerificationEmailPlease'));
        }
      }}
      onInputChange={(field: string, value: string) => updateFormData(field as keyof typeof formData, value)}
    />
  );

  const renderPasskeyCard = () => (
    <PasskeyAuth
      email={formData.email}
      isAuthenticated={false}
      onSuccess={(userData) => {
        if (userData.authenticated) {
          displayToast(AUTH_MESSAGES.PASSKEY_LOGIN_SUCCESS, 'success', 1500);
          // Don't call handleSuccessfulLogin() - let the auth state change trigger navigation
        } else if (userData.registered) {
          displayToast('Passkey registered successfully!', 'success', 3000);
          setActiveCard('login');
        }
      }}
      onError={(error) => {
        displayToast(error.message, 'danger', 5000);
      }}
      onGoBack={() => { setActiveCard('login'); resetForm(); }}
    />
  );

  const renderPasskeySetupCard = () => (
    <PasskeyAuth
      email={formData.email}
      isAuthenticated={false}
      onSuccess={(userData) => {
        if (userData.authenticated) {
          displayToast(AUTH_MESSAGES.PASSKEY_LOGIN_SUCCESS, 'success', 1500);
          // Don't call handleSuccessfulLogin() - let the auth state change trigger navigation
        } else if (userData.registered) {
          displayToast('Passkey registered successfully! You can now login with your passkey.', 'success', 3000);
          // Set passkey mode preference so user sees passkey login option
          localStorage.setItem('preferPasskey', 'true');
          setActiveCard('login');
        }
      }}
      onError={(error) => {
        displayToast(error.message, 'danger', 5000);
      }}
      onGoBack={() => { setActiveCard('login'); resetForm(); }}
    />
  );

  const renderRegisterCard = () => (
    <RegisterCard
      formData={{
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      }}
      uiState={{ 
        loggingIn: uiState.loggingIn,
        registering: uiState.registering,
        showPassword: uiState.showPassword
      }}
      authLoading={authLoading}
      isDisabled={isDisabled}
      onGoBack={() => { setActiveCard('main'); resetForm(); }}
      onRegister={handleRegisterClick}
      onContinue={() => setActiveCard('registerPassword')}
      onInputChange={(field: string, value: string) => updateFormData(field as keyof typeof formData, value)}
    />
  );

  const renderRegisterPasswordCard = () => (
    <RegisterPasswordCard
      formData={{
        password: formData.password,
        confirmPassword: formData.confirmPassword
      }}
      uiState={{ 
        registering: uiState.registering
      }}
      authLoading={authLoading}
      isDisabled={isDisabled}
      onGoBack={() => setActiveCard('register')}
      onRegister={handleRegisterClick}
      onInputChange={(field: string, value: string) => updateFormData(field as keyof typeof formData, value)}
    />
  );

  const renderVerificationCodeCard = () => (
    <VerificationCodeCard
      email={formData.email}
      isVerifying={isVerifying}
      initialMode={verificationMode}
      onGoBack={() => setActiveCard('login')}
      onVerifyEmailCode={handleEmailVerification}
      onVerifyEmployeeCode={handleEmployeeCodeVerification}
      onResendCode={async () => {
        try {
          const result = await resendVerification(formData.email);
          if (result.success) {
            displayToast('Verification email sent! Please check your inbox.', 'success', 4000);
          } else {
            displayToast(result.error?.toString() || t('pages.login.LoginReg.failedToSendVerificationEmail'));
          }
        } catch {
          displayToast(t('pages.login.LoginReg.failedToSendVerificationEmailPlease'));
        }
      }}
    />
  );

  const renderContent = () => {
    switch (activeCard) {
      case 'login':
        return renderLoginCard();
      case 'password':
        return renderPasswordCard();
      case 'register':
        return renderRegisterCard();
      case 'registerPassword':
        return renderRegisterPasswordCard();
      case 'reset':
        return renderResetCard();
      case 'passkey':
        return renderPasskeyCard();
      case 'passkeySetup':
        return renderPasskeySetupCard();
      case 'verifyCode':
        return renderVerificationCodeCard();
      case 'main':
      default:
        return renderMainCard();
    }
  };

  // If user is already logged in, don't render login UI
  if (isLoggedIn && user) {
    return null;
  }

  return (
    <IonPage className={`${styles.loginPage} auth-screen`}>
      <IonContent scrollY={false} className={styles.loginContent}>
        {renderContent()}

        <IonToast
          isOpen={toastState.show}
          onDidDismiss={() => setToastState(prev => ({ ...prev, show: false }))}
          message={toastState.message}
          duration={toastState.duration}
          // Success at the bottom in the app's blue gradient; anything else (errors, warnings) at the top in the red one
          position={toastState.color === 'success' ? 'bottom' : 'top'}
          cssClass={toastState.color === 'success' ? TOAST_CLASS_OK : TOAST_CLASS_ERROR}
        />
        <div style={{ position: 'absolute', bottom: 'calc(12px + var(--ion-safe-area-bottom, env(safe-area-inset-bottom, 0px)))', width: '100%', textAlign: 'center', fontSize: '11px', zIndex: 10 }}>
          <span
            onClick={() => router.push('/privacy')}
            style={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer', margin: '0 8px' }}
          >{t('pages.login.LoginReg.privacyPolicy')}</span>
          <span style={{ color: 'rgba(255,255,255,0.4)' }}>·</span>
          <span
            onClick={() => router.push('/terms')}
            style={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer', margin: '0 8px' }}
          >{t('pages.login.LoginReg.termsOfService')}</span>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LoginReg;
